import React, { useCallback, useEffect, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { moneyFormat, validaCPF, validarCNPJ } from '~/utils/functions';
import { useFocusTabFinalizacao } from '../../hooks/useFocusTabFinalizacao';
import { usePdvOnline } from '../../hooks/usePdvOnline';
import { pedidoService } from '../../services/pedido';
import { tabFinalizacaoServices } from '../../services/tab-finalizacao';
import { tabKeysMappings } from '../../utils/tabKeysMappings';
import { getSaldoCaixa } from '../../services/getSaldoCaixa';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';
import { formataValorNumerico } from '../FormaPagamento/utils/formataValorNumerico';
import { FinalizadorasPedidoProps } from '../../types/services';
import { CadastroCliente } from '../../types';

import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

const MySwal = withReactContent(Swal);

export const ButtonFinalizar: React.FC = () => {
  const [isMounted, setIsMounted] = useState(true);

  const {
    resta,
    finalizadorasTable,
    initialStatePdv,
    produtosTable,
    handleStatusPedido,
    pessoa,
    pedido,
    finalizadorasPedido,
    onOpenModal,
    finalizacaoContainertRef,
    handleActiveTab,
    handleInfoPedido,
    handleSaldoPdv,
    pedidoPendenteSelected,
    resetPedidoPendente,
    formTabFinalizacao,
    troco,
    val_acrescimo,
    val_desconto,
    handleInitialStatePdv,
    qtdParcelas,
    bandeiraSelecionada,
  } = usePdvOnline();

  function removerDuplicadosPorChave<T>(array: T[], key: keyof T): T[] {
    const map = new Map<any, T>();

    array.forEach((item) => {
      map.set(item[key], item);
    });

    return Array.from(map.values());
  }

  const { handleInputFocus } = useFocusTabFinalizacao();

  const naoComletaVenda =
    resta > 0 ||
    finalizadorasTable.length <= 0 ||
    produtosTable.length <= 0 ||
    pedido.length <= 0;

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  const validateCpfCnpj = useCallback((numero: string) => {
    if (numero === '') return false;

    if (numero.length < 14 || numero.length > 18)
      return 'Número de CPF/CNPJ inválido';

    if (numero.length === 14) {
      if (!validaCPF(numero)) return 'O CPF informado é inválido';
      return false;
    }

    if (numero.length === 18) {
      if (!validarCNPJ(numero)) return 'O CNPJ informado é inválido';
      return false;
    }
  }, []);

  const handleFinalizaVenda = useCallback(async () => {
    if (finalizadorasTable.length === 0) {
      const tabFinalizacaoButton = document.querySelector<HTMLButtonElement>(
        tabKeysMappings[2],
      );
      if (tabFinalizacaoButton) {
        tabFinalizacaoButton?.click();
        handleActiveTab('FINALIZACAO');
      }
      toast.warning('Forma de Pagamento deve ser informada');
      return;
    }

    if (resta > 0 && finalizadorasTable.length > 0) {
      const tabFinalizacaoButton = document.querySelector<HTMLButtonElement>(
        tabKeysMappings[2],
      );
      if (tabFinalizacaoButton) {
        tabFinalizacaoButton.click();
        handleActiveTab('FINALIZACAO');
      }
      toast.warning(
        `Valor restante pendente informar: R$ ${moneyFormat(resta.toFixed(2))}`,
      );
      return;
    }

    if (Math.abs(Number(troco.toFixed(2))) > 0) {
      const finalizadoraDinheiro = removerDuplicadosPorChave(
        finalizadorasTable,
        'id',
      ).find(
        (f) => f.tipo_finalizadora === 0 && f.des_finalizadora === 'DINHEIRO',
      );

      if (finalizadoraDinheiro) {
        const valParcela = formataValorNumerico(
          finalizadoraDinheiro.vlr_parcela,
        );

        if (valParcela < Math.abs(troco)) {
          toast.warning(
            'Para troco, deve haver uma forma de pagamento do tipo DINHEIRO com valor suficiente',
          );
          return;
        }
      }
      // } else {
      //   toast.warning(
      //     'Nenhuma forma de pagamento do tipo DINHEIRO informada para troco',
      //   );
      //   return;
      // }
    }

    if (naoComletaVenda || !initialStatePdv || !isMounted) return;

    let cod_pessoa = pessoa?.cod_pessoa ?? null;

    const validateCpfCnpjInput = async () => {
      const inputCpfCnpj =
        finalizacaoContainertRef.current?.querySelector<HTMLInputElement>(
          '[name="num_cpf_cnpj"]',
        );

      if (inputCpfCnpj) {
        const numero = inputCpfCnpj.value;
        const validationMessage = validateCpfCnpj(numero || '');

        if (validationMessage) {
          handleInputFocus('num_cpf_cnpj');
          toast.warning(validationMessage);
          return false;
        }

        if (numero) {
          const { data } = await tabFinalizacaoServices.getClienteCpfCnpj(
            numero.replace(/[^a-zA-Z0-9]/g, ''),
          );

          if (!data) {
            handleStatusPedido('CADASTRANDO_CLIENTE_AUTOMATICO');

            const isCnpj = (numero ?? '').replace(/[_\-./]/g, '').length > 11;

            const payload: CadastroCliente = {
              nome_pessoa: 'CADASTRO AUTOMATICO',
              tipo_pessoa: isCnpj ? 1 : 0,
              num_cpf: isCnpj ? undefined : numero,
              num_cnpj: isCnpj ? numero : undefined,
              email: '',
              des_logradouro: 'A DEFINIR',
              num_endereco: '0',
              des_bairro: 'A DEFINIR',
              des_complemento: 'A DEFINIR',
              flg_endereco_principal: true,
              cod_loja: Number(initialStatePdv.cod_loja),
            };

            const novoCliente = await tabFinalizacaoServices.insereCliente(
              payload,
            );

            if (novoCliente.success) {
              const match = novoCliente.message.match(/^#(\d+)/);
              cod_pessoa = match ? Number(match[1]) : null;
              return true;
            }

            handleInputFocus('num_cpf_cnpj');
            return false;
          }
        }
      }
      return true;
    };

    if (!(await validateCpfCnpjInput())) return;

    handleStatusPedido('LANCAMENTO_INCIADO');

    const atualizarSaldo = async () => {
      const { saldo } = await getSaldoCaixa(
        Number(initialStatePdv.cod_operador),
        Number(initialStatePdv.num_pdv),
        Number(initialStatePdv.cod_loja),
      );

      handleInitialStatePdv({ ...initialStatePdv, val_saldo: saldo });
      handleSaldoPdv(moneyFormat(saldo.toString()));
    };

    const processarPedido = async () => {
      const finalizadorasCleaned = removerDuplicadosPorChave(
        finalizadorasPedido,
        'id',
      );

      const getTipoFinalizadora = (codigo: number): number =>
        finalizadorasTable.find((fin) => fin.cod_finalizadora === codigo)
          ?.tipo_finalizadora ?? 0;

      let finalizadoras: FinalizadorasPedidoProps[] = [];

      if (Math.abs(troco) > 0) {
        finalizadoras = finalizadorasCleaned.map((f) => {
          const tipo_finalizadora = getTipoFinalizadora(f.cod_finalizadora);

          if (f.cod_finalizadora === 1 && f.num_condicao === 0) {
            return {
              ...f,
              val_parcela: f.val_parcela,
              tipo_finalizadora,
            };
          }

          return {
            ...f,
            tipo_finalizadora,
          };
        });
      } else {
        finalizadoras = finalizadorasCleaned.map((f) => {
          const tipo_finalizadora = getTipoFinalizadora(f.cod_finalizadora);

          return {
            ...f,
            tipo_finalizadora,
          };
        });
      }

      // 0 - Dinheiro; 1 - Cartão/PIX; 5 - Ticket; 7 - Contra-Vale
      const finalizadorasQueNaoExigemCliente = [0, 1, 5, 7];

      const exigeCliente = finalizadoras.some(
        (parcela: any) =>
          !finalizadorasQueNaoExigemCliente.includes(parcela.tipo_finalizadora),
      );

      if (exigeCliente && !cod_pessoa) {
        toast.warning('Finalizadora exige que cliente seja identificado!');
        handleStatusPedido('CLIENTE_OBRIGATORIO_FINALIZADORA');
        handleInputFocus('num_cpf_cnpj');

        setTimeout(() => {
          handleStatusPedido(null);
        }, 3000);

        return;
      }

      const valFinalizadoras = finalizadoras
        .filter((f: any) => f.tipo_finalizadora !== 0)
        .reduce((total: number, f: any) => total + f.val_parcela, 0);

      if (troco > 0 && valFinalizadoras > 0) {
        const { saldo } = await getSaldoCaixa(
          Number(initialStatePdv.cod_operador),
          Number(initialStatePdv.num_pdv),
          Number(initialStatePdv.cod_loja),
        );

        if (saldo < valFinalizadoras) {
          const result = await MySwal.fire({
            title: 'Saldo insuficiente para troco',
            text: 'O saldo do caixa é insuficiente para o valor do troco. Deseja continuar mesmo assim?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'SIM',
            cancelButtonText: 'NÃO',
            confirmButtonColor: '#009E0F',
            cancelButtonColor: '#CF2A27',
            didOpen: () => {
              const modals = document.querySelectorAll('.modal');
              modals.forEach((m) => m.removeAttribute('tabindex'));

              Swal.getConfirmButton()?.focus();
            },
            didClose: () => {
              const modals = document.querySelectorAll('.modal');
              modals.forEach((m) => m.setAttribute('tabindex', '-1'));
            },
          });

          if (!result.isConfirmed) {
            handleStatusPedido(null);
            return;
          }
        }
      }

      const { des_observacao } = formTabFinalizacao.getValuesTabFinalizacao();

      if (!pedidoPendenteSelected) {
        const {
          success,
          data: { cod_seq_pedido, num_pedido, tipo_status },
        } = await pedidoService.createPedido({
          cod_loja: Number(initialStatePdv.cod_loja),
          cod_pessoa,
          num_pdv: Number(initialStatePdv.num_pdv),
          cod_operador: Number(initialStatePdv.cod_operador),
          itemsPedido: pedido,
          finalizadoras,
          des_observacao,
          val_acrescimo,
          val_desconto,
          qtd_parcela: qtdParcelas,
          val_troco: troco,
          bandeiraSelecionada: bandeiraSelecionada ?? [],
        });

        if (success) {
          handleStatusPedido('LANCAMENTO_FINALIZADO');
          onOpenModal('pedidoRegistrado');
          handleInfoPedido(cod_seq_pedido, num_pedido, tipo_status);
          await atualizarSaldo();
          return;
        }
      } else {
        const { num_pdv, cod_operador, dta_emissao, cod_seq_pedido } =
          pedidoPendenteSelected;
        const update = await pedidoService.updatePedidoPendente(
          num_pdv ?? Number(initialStatePdv.num_pdv),
          cod_operador ?? Number(initialStatePdv.cod_operador),
          dta_emissao,
          cod_seq_pedido,
          Number(initialStatePdv.cod_loja),
          cod_pessoa,
          pedido,
          finalizadoras,
          des_observacao,
          qtdParcelas,
          troco,
          bandeiraSelecionada ?? [],
        );

        if (update.success) {
          handleStatusPedido('LANCAMENTO_FINALIZADO');
          onOpenModal('pedidoRegistrado');
          handleInfoPedido(
            cod_seq_pedido,
            update.data.num_pedido,
            update.data.tipo_status,
          );
          await atualizarSaldo();

          setTimeout(resetPedidoPendente, 200);
          toast.success(update.message);
          return;
        }
      }

      handleStatusPedido('FALHA_LANCAMENTO');
    };

    await processarPedido();
  }, [
    finalizadorasTable,
    resta,
    troco,
    naoComletaVenda,
    initialStatePdv,
    isMounted,
    handleStatusPedido,
    handleActiveTab,
    finalizacaoContainertRef,
    validateCpfCnpj,
    handleInputFocus,
    handleSaldoPdv,
    finalizadorasPedido,
    formTabFinalizacao,
    pedidoPendenteSelected,
    pessoa,
    pedido,
    onOpenModal,
    handleInfoPedido,
    resetPedidoPendente,
    resta,
    troco,
    handleInitialStatePdv,
    bandeiraSelecionada,
  ]);

  useEffect(() => {
    const handleKeyFinalizaVenda = async (ev: KeyboardEvent) => {
      if (produtosTable.length > 0 && ev.key === 'F9') {
        ev.preventDefault();
        await handleFinalizaVenda();
      }
    };

    window.addEventListener('keydown', handleKeyFinalizaVenda);
    return () => window.removeEventListener('keydown', handleKeyFinalizaVenda);
  }, [handleFinalizaVenda, produtosTable.length]);

  return (
    <CustomButtonNovo
      onClick={handleFinalizaVenda}
      disabled={produtosTable.length <= 0}
      icon={FaCheck}
      label="Finalizar (F9)"
      style={{ backgroundColor: '#019f11', textTransform: 'uppercase' }}
      width="auto"
    />
  );
};
